import { createContext, useCallback, useEffect } from 'react';
import { useStateIfMounted } from 'use-state-if-mounted';
import { getBalance } from '@wagmi/core';

import wagmiConfig from 'wagmiConfig';
import { BALANCE_TOKEN_LIST } from 'constants/index';
import { TOKEN_PRICE_KEYS, STABLECOINS } from 'constants/iconMappings';
import useWalletContext from 'hooks/useWalletContext';
import useWindowContext from 'hooks/useWindowContext';
import { setBalanceWithDecimals } from 'utils/numbers';
import { useLPBondsPrices } from 'hooks/useLPBondsPrices';

export const BalanceContext = createContext(null);

export const BalanceProvider = ({ children }) => {
  const [balances, setBalances] = useStateIfMounted([]);
  const { address, chainId, provider } = useWalletContext();
  const {
    coinPrice,
    ethPrice,
    nrgPrice,
    apePrice,
    gmiPrice,
    helloPrice,
    kimaPrice,
    monPrice,
    bnbPrice,
    arbPrice,
  } = useLPBondsPrices();
  const { active } = useWindowContext();

  const totalBalance = balances?.reduce((a, b) => a + b.value, 0);

  const getTokenBalances = useCallback(
    async (retryCount = 0) => {
      try {
        const promise = BALANCE_TOKEN_LIST.map(async (token) => {
          let balance = 0;
          let result;
          if (token.chains.includes(chainId)) {
            // Check if token is native on this specific chain
            const isNativeOnThisChain =
              token.isNative && (!token.addresses || !token.addresses[chainId]);

            if (isNativeOnThisChain) {
              // For native tokens (like ETH or NRG on Energi chains)
              result = await getBalance(wagmiConfig, { address, chainId });
            } else {
              // For ERC20 tokens (like NRG on Ethereum)
              let tokenAddress = token.addresses?.[chainId];
              if (tokenAddress) {
                result = await getBalance(wagmiConfig, {
                  address,
                  token: tokenAddress,
                  chainId,
                });
              }
            }
          }
          if (result?.value) {
            balance = setBalanceWithDecimals(result.value, result.decimals);
          }
          // Get token price from config
          const prices = {
            ethereum: ethPrice,
            energi: nrgPrice,
            apecoin: apePrice,
            gonnamakeit: gmiPrice,
            'hello-labs': helloPrice,
            kima: kimaPrice,
            monad: monPrice,
            binancecoin: bnbPrice,
            arbitrum: arbPrice,
          };
          const priceKey = TOKEN_PRICE_KEYS[token.symbol];
          let tokenPrice;

          if (STABLECOINS.includes(token.symbol)) {
            tokenPrice = 1;
          } else if (priceKey) {
            tokenPrice = prices[priceKey] || coinPrice || 0;
          } else {
            tokenPrice = coinPrice || 0;
          }

          return {
            ...token,
            balance: balance, // Store raw number, format with commas on display
            last_price: tokenPrice,
            value: Math.round(balance * tokenPrice * 100) / 100, // Round to 2 decimal places
          };
        });
        const balances = await Promise.all(promise);

        // Show all tokens that belong to the current chain (already filtered by chains.includes(chainId) above)
        setBalances(balances);
      } catch (error) {
        console.error('Error fetching balances:', error);

        if (retryCount < 3) {
          setTimeout(() => getTokenBalances(retryCount + 1), 2000);
        }
      }
    },
    [
      address,
      chainId,
      coinPrice,
      ethPrice,
      nrgPrice,
      apePrice,
      gmiPrice,
      helloPrice,
      kimaPrice,
      monPrice,
      bnbPrice,
      arbPrice,
      provider,
    ],
  );

  useEffect(() => {
    if (!active || !address) {
      return;
    }

    getTokenBalances();
  }, [active, address, getTokenBalances]);

  useEffect(() => {
    if (address && chainId) {
      getTokenBalances();
    }
  }, [
    address,
    chainId,
    coinPrice,
    ethPrice,
    nrgPrice,
    apePrice,
    gmiPrice,
    helloPrice,
    kimaPrice,
    monPrice,
    bnbPrice,
    arbPrice,
    getTokenBalances,
  ]);

  // Find native and wrapped tokens dynamically based on current chain
  const nativeToken = balances.find((token) => token.isNative && token.chains.includes(chainId));
  const wrappedToken = balances.find(
    (token) => (token.isWrapped || token.symbol.startsWith('W')) && token.chains.includes(chainId),
  );

  return (
    <BalanceContext.Provider
      value={{
        totalBalance,
        balances,
        getTokenBalances,
        wrappedBalance: wrappedToken?.balance,
        nativeBalance: nativeToken?.balance,
        coinLastPrice: nativeToken?.last_price,
      }}
    >
      {children}
    </BalanceContext.Provider>
  );
};
